import { Button, Input } from '@westhetics/ui'
import { useState, type FormEvent } from 'react'
import { brDateToIso, isoToBrDate, maskDate } from '@/lib/dates'

/**
 * Filtro de período das listas (docs/styleguide/03-componentes.md · 08): duas datas digitadas
 * como dd/mm/aaaa e o "Filtrar" em contorno, que só habilita quando algo mudou. As datas chegam e
 * saem em `aaaa-mm-dd`; a conferência acontece aqui, antes de ir à API. Com `required`, as duas
 * datas são obrigatórias (a trilha abre sempre num período); sem ele, cada ponta pode ficar em
 * branco e o botão diz o que falta.
 */
export function PeriodFilter({
  from,
  to,
  onApply,
  fromLabel,
  toLabel,
  required = false,
}: {
  from: string
  to: string
  onApply: (from: string, to: string) => void
  /** O que a data inicial filtra, para quem navega sem o mouse: "Atendimentos a partir de". */
  fromLabel: string
  /** "Atendimentos até". */
  toLabel: string
  required?: boolean
}) {
  const [start, setStart] = useState(isoToBrDate(from))
  const [end, setEnd] = useState(isoToBrDate(to))
  const [error, setError] = useState<string | null>(null)

  function apply(event: FormEvent) {
    event.preventDefault()
    const startIso = !required && start.trim() === '' ? '' : brDateToIso(start)
    const endIso = !required && end.trim() === '' ? '' : brDateToIso(end)
    if (startIso === null || endIso === null) {
      setError(required ? 'Use o formato dd/mm/aaaa nas duas datas.' : 'Use o formato dd/mm/aaaa.')
      return
    }
    if (startIso && endIso && startIso > endIso) {
      setError('O período precisa começar antes de terminar.')
      return
    }
    setError(null)
    onApply(startIso, endIso)
  }

  const isUnchanged = start === isoToBrDate(from) && end === isoToBrDate(to)
  return (
    <form onSubmit={apply} noValidate>
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-[124px]">
          <Input
            aria-label={fromLabel}
            aria-invalid={error ? true : undefined}
            inputMode="numeric"
            placeholder="dd/mm/aaaa"
            value={start}
            onChange={(event) => setStart(maskDate(event.target.value))}
          />
        </div>
        <span aria-hidden className="text-label">
          —
        </span>
        <div className="w-[124px]">
          <Input
            aria-label={toLabel}
            aria-invalid={error ? true : undefined}
            inputMode="numeric"
            placeholder="dd/mm/aaaa"
            value={end}
            onChange={(event) => setEnd(maskDate(event.target.value))}
          />
        </div>
        {/* Desabilitado diz o motivo no rótulo. */}
        <Button type="submit" variant="outline" disabled={isUnchanged}>
          {!isUnchanged ? 'Filtrar' : from || to ? 'Período aplicado' : 'Informe o período'}
        </Button>
      </div>
      {error ? (
        <p
          role="alert"
          className="font-body text-eyebrow text-plum mt-2 leading-[1.5] font-light tracking-normal"
        >
          {error}
        </p>
      ) : null}
    </form>
  )
}
